import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { messageFromApiErrorBody } from '../../common/apiErrorMessage';
import { httpClient } from '../../common/httpclient';

const POLL_INTERVAL_MS = 5000;
// Logo após "connect" o provedor ainda pode responder "desconectado" até a sessão subir e gerar o QR code.
const START_POLL_INTERVAL_MS = 1500;
const START_MAX_ATTEMPTS = 10;

const STATUS_LABEL = {
    connected: { label: 'Conectado', color: 'success' },
    connecting: { label: 'Aguardando leitura do QR code', color: 'warning' },
    disconnected: { label: 'Desconectado', color: 'default' },
};

// A API responde erros com texto puro (http.Error) ou JSON `{ error }`.
function errorMessage(error, fallback) {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    return messageFromApiErrorBody(data) || fallback;
}

// Formata dígitos do JID (55DDNNNNNNNNN) para exibição.
function formatPhone(digits) {
    if (!digits) return '';
    const m = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(digits);
    return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : `+${digits}`;
}

// Conteúdo da conexão do WhatsApp (status, QR code, conectar/desconectar).
// Renderizado dentro de um card da aba "Integrações" em `pages/Settings.js`;
// `onNotify(message)` exibe o feedback no snackbar da tela de configurações.
// `basePath` permite reusar o componente para o WhatsApp do sistema (`/admin/whatsapp`).
export default function WhatsappConnection({ onNotify, basePath = '/whatsapp' }) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [startAttempts, setStartAttempts] = useState(null);

    const fetchStatus = useCallback(async () => {
        try {
            const response = await httpClient.get(basePath);
            setState(response.data);
            setError('');
        } catch (err) {
            setError(errorMessage(err, 'Não foi possível consultar o status do WhatsApp.'));
        } finally {
            setLoading(false);
        }
    }, [basePath]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // Polling do status (sem webhook) enquanto aguarda a leitura do QR code.
    const connecting = state?.status === 'connecting';
    useEffect(() => {
        if (!connecting) return undefined;
        const timer = setInterval(() => {
            if (!document.hidden) fetchStatus();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [connecting, fetchStatus]);

    // Aguardando a sessão iniciar: consulta o status em intervalo curto até sair de "desconectado".
    const starting = startAttempts !== null;
    useEffect(() => {
        if (!starting) return undefined;
        if (state?.status !== 'disconnected' || startAttempts >= START_MAX_ATTEMPTS) {
            setStartAttempts(null);
            return undefined;
        }
        const timer = setTimeout(async () => {
            await fetchStatus();
            setStartAttempts((n) => (n === null ? null : n + 1));
        }, START_POLL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [starting, startAttempts, state, fetchStatus]);

    const handleConnect = async () => {
        try {
            setBusy(true);
            const response = await httpClient.post(`${basePath}/connect`);
            setState(response.data);
            setError('');
            setStartAttempts(response.data?.status === 'disconnected' ? 0 : null);
        } catch (err) {
            setError(errorMessage(err, 'Não foi possível iniciar a conexão do WhatsApp.'));
        } finally {
            setBusy(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            setBusy(true);
            const response = await httpClient.post(`${basePath}/disconnect`);
            setState(response.data);
            setError('');
            if (onNotify) onNotify('WhatsApp desconectado.');
        } catch (err) {
            setError(errorMessage(err, 'Não foi possível desconectar o WhatsApp.'));
        } finally {
            setBusy(false);
        }
    };

    const status = state?.status || 'disconnected';
    const statusInfo = STATUS_LABEL[status] || STATUS_LABEL.disconnected;
    const notEnabled = state && state.enabled === false;

    return (
        <Box sx={{ pt: '14px' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {notEnabled && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    A integração com WhatsApp não está habilitada neste ambiente.
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '6px' }}>
                                Status
                            </Typography>
                            <Chip size="small" label={statusInfo.label} color={statusInfo.color} />
                        </Box>
                        {(status === 'connected' || state?.phone_number) && (
                            <Box>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{formatPhone(state?.phone_number)}</Typography>
                            </Box>
                        )}
                    </Box>

                    {starting && status === 'disconnected' && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <CircularProgress size={28} />
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 1.5 }}>
                                Iniciando a conexão e gerando o QR code…
                            </Typography>
                        </Box>
                    )}

                    {status === 'connecting' && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            {state?.qr_code ? (
                                <Box component="img" src={state.qr_code} alt="QR code do WhatsApp" sx={{ width: 260, height: 260, maxWidth: '100%' }} />
                            ) : (
                                <CircularProgress size={28} />
                            )}
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 1.5 }}>
                                No celular, abra o WhatsApp &gt; Aparelhos conectados &gt; Conectar um aparelho e leia o QR code.
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', mt: 3 }}>
                        {status === 'connected' ? (
                            <Button variant="outlined" color="error" size="small" onClick={handleDisconnect} disabled={busy}>
                                Desconectar
                            </Button>
                        ) : (
                            <Button variant="contained" size="small" onClick={handleConnect} disabled={busy || starting || notEnabled}>
                                {status === 'connecting' ? 'Gerar novo QR code' : 'Conectar WhatsApp'}
                            </Button>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
}
